<template>
  <div class="gh-credential-landing-page">
    <div class="gh-hero">
      <div class="container">
        <div class="gh-hero-content">
          <h1 class="gh-hero-heading">Take a <span>deep-dive</span> with some of the world’s leading mental health & wellbeing experts.</h1>
          <ul class="gh-hero-list">
            <li class="gh-list-item">
              <div class="gh-list-icon">
                <img src="/images/app/mini-icon-skills.svg" alt="Skills Icon" width="100%">
              </div>
              <h2 class="gh-list-title">Learn new skills</h2>
            </li>
            <li class="gh-list-item">
              <div class="gh-list-icon">
                <img src="/images/app/mini-icon-connect.svg" alt="Connect Icon" width="100%">
              </div>
              <h2 class="gh-list-title">Connect with peers</h2>
            </li>
            <li class="gh-list-item">
              <div class="gh-list-icon">
                <img src="/images/app/mini-icon-credential.svg" alt="Credential Icon" width="100%">
              </div>
              <h2 class="gh-list-title">Earn a Digital Credential</h2>
            </li>
          </ul>
        </div>
        <div class="gh-hero-image">
          <img src="/images/credential/main-banner-image-group.png" alt="Credential Course Authors" width="100%" loading="lazy">
        </div>
      </div>
    </div>
    <div class="gh-section gh-intro">
      <div class="container">
        <div class="gh-intro-content">
          <p>MHA Micro-credential Courses are self-paced, <strong>deep-dive, content-rich</strong> (20+ hours of learning) programs led by internationally-renowned experts.</p>
          <p>
            Each course is meticulously crafted and contains a range of learning resources including video lectures, peer-reviewed articles, case studies, workbooks, assessments, practical activities, and more. Micro-credentials are 100% self-paced and accessible on-demand, so learners can start at any time and progress at their own pace.
          </p>
          <p>
            Upon completion of an MHA Micro-credential Course, learners are issued with an <strong>MHA Digital Badge</strong> (an internationally-verifiable digital credential) and a CPD Certificate of Attainment.
          </p>
        </div>
        <div class="gh-intro-image">
          <img src="/images/credential/Circular-Purple-Graphic.svg" alt="100% self-paces; 20+ hours of learning; global experts" width="100%" loading="lazy">
        </div>
      </div>
    </div>
    <div class="gh-section gh-features">
      <div class="container">
        <ul class="gh-features-list">
          <li v-for="item in features" :key="item.title" class="gh-feature-item">
            <div class="gh-feature-icon">
              <img :src="item.icon" :alt="item.title" width="100%" loading="lazy">
            </div>
            <h3 class="gh-feature-title" v-html="item.title"></h3>
            <div class="gh-feature-text" v-html="item.text"></div>
          </li>
        </ul>
      </div>
    </div>
    <div class="gh-section gh-courses">
      <div class="container">
        <ul class="gh-courses-list">
          <li v-for="(course, index) in courses" :key="course.id" :class="courseClassList(index)">
            <div class="gh-course-info">
              <h3 class="gh-course-title">{{ course.title }}</h3>
              <div class="gh-course-overview" v-html="course.cardText"></div>
              <a class="gh-button-redesign gh-button-redesign--primary" :href="course.lzRoute" target="_blank">Learn More</a>
            </div>
            <div class="gh-course-image">
              <img :src="course.links.image" :alt="course.title" loading="lazy" width="100%">
            </div>
          </li>
        </ul>
      </div>
    </div>

    <gh-testimonials :items="testimonials"/>

    <div class="gh-section gh-contact">
      <div class="container">
        <h2 class="gh-heading">Got a question?</h2>
        <h3 class="gh-subheading">Email us to find out more about our Micro-credentials.</h3>
        <gh-contact-form/>
      </div>
    </div>
  </div>
</template>
<script>
  export default {
    name: 'gh-credential-catalogue',

    components: {
      ghTestimonials: () => import(/* webpackChunkName: "credentialCourse" */ '../../components/Testimonials.vue'),
      ghContactForm: () => import(/* webpackChunkName: "credentialCourse" */ './contactForm.vue')
    },

    data() {
      return {
        courses: [],
      }
    },

    computed: {
      features () {
        return [
          {
            title: 'Learn directly from the best',
            icon: '/images/credential/icon-learn-best.svg',
            text: 'Courses led by internationally-<br>renowned experts.'
          },
          {
            title: 'In-depth learning journey',
            icon: '/images/credential/icon-learning-journey.svg',
            text: 'Explore video lectures, journal<br>articles, case studies, activities &<br>more.'
          },
          {
            title: 'Take your skills to a new level',
            icon: '/images/credential/icon-new-level.svg',
            text: 'Gain expert knowledge and<br>unique insights from experienced<br>clinicians.'
          },
          {
            title: 'Lifetime, on-demand access',
            icon: '/images/credential/icon-on-demand.svg',
            text: 'Start any time. Learn from<br>anywhere. Progress at your own<br>pace.'
          },
          {
            title: 'Earn a Digital Credential<br>(MHA Digital Badge)',
            icon: '/images/credential/icon-digital-credential.svg',
            text: 'Receive a Digital Badge & CPD<br>Certificate. Accrue CPD hours/<br>points.'
          },
          {
            title: 'International learning<br>community',
            icon: '/images/credential/icon-international-learning.svg',
            text: 'Learn, share and network with<br>an international community of<br>learners.'
          },
          {
            title: 'Practical tools for real-life challenges',
            icon: '/images/credential/icon-practical-tools.svg',
            text: 'Expand your practical toolkit<br>through case studies, interviews,<br>and more.'
          }
        ]
      },

      testimonials () {
        return _.chain(this.courses)
            .flatMap(course => {
              return course.testimonials.map(testimonial => {
                testimonial.courseTitle = course.title
                return testimonial
              })
            })
            .shuffle()
            .value()
      }
    },

    async created() {
      await this.fetchCourses()
    },

    methods: {
      fetchCourses() {
        return axios
            .get(`/api/v1/credential/courses?include=testimonials`)
            .then(({data}) => {
              this.courses = data
            })
            .catch(() => this.$router.replace('/'))
      },

      courseClassList (index) {
        return [
          'gh-course-item',
          index % 2 !== 0 ? 'is-flip' : ''
        ]
      }
    }
  }
</script>

<style lang="scss" scoped>
  .gh-credential-landing-page {
    font-family: 'proxima-nova', sans-serif;
  }

  .gh-hero {
    background: #E7F6F5;

    .container {
      display: flex;
      align-items: center;
    }
  }

  .gh-hero-content {
    width: 50%;
    padding-right: 2rem;
  }

  .gh-hero-heading {
    margin-bottom: 3rem;
    font-weight: 600;
    font-size: 2.5rem;
    line-height: 1.1;
    color: #51499D;

    span {
      position: relative;
      &::before {
        position: absolute;
        left: 0;
        bottom: 8px;
        content: '';
        width: 100%;
        height: 1px;
        border-bottom: 1px solid #51499D;
      }
    }
  }

  .gh-list-item {
    display: flex;
    align-items: center;

    + .gh-list-item {
      margin-top: 1rem;
    }
  }

  .gh-list-icon {
    width: 24px;
    height: 24px;
    margin-right: .5rem;
  }

  .gh-list-title {
    font-size: 1.7rem;
    color: #085078;
  }

  .gh-intro {
    .container {
      display: flex;
      align-items: center;
      padding: 0 5rem;
    }
  }

  .gh-intro-content {
    width: 55%;
    padding-right: 2rem;
    p {
      font-size: 1.2rem;
      + p {
        margin-top: 1rem;
      }
    }
  }

  .gh-intro-image {
    width: 320px;
  }

  .gh-features {
    background: #F4F7FA;
    padding-bottom: 0;
  }

  .gh-features-list {
    display: flex;
    align-items: stretch;
    justify-content: center;
    flex-flow: row wrap;
  }

  .gh-feature-item {
    width: 25%;
    margin-bottom: 3rem;
    text-align: center;
  }

  .gh-feature-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto .5rem;
  }

  .gh-feature-title {
    margin-bottom: 1rem;
    font-size: 1.2rem;
    line-height: 1.2;
    color: #085078;
  }

  .gh-feature-text {
    line-height: 1.5;
  }

  .gh-courses-list {
    margin-top: -2rem;
  }

  .gh-course-item {
    padding: 2rem 0;
    display: flex;
    align-items: center;
    + .gh-course-item {
      border-top: 1px solid rgba(#000, .2);
    }
  }

  .gh-course-info {
    width: 65%;
    flex-shrink: 0;
    padding-right: 5rem;
  }

  .gh-course-title {
    margin-bottom: 1rem;
    font-size: 1.8rem;
    color: #085078;
  }

  .gh-course-overview {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
    line-height: 1.5;
    font-weight: 300;
  }

  .gh-course-image {
    border-radius: 5px;
    overflow: hidden;
  }

  .gh-course-item.is-flip {
    .gh-course-info {
      order: 1;
      padding-right: 0;
      padding-left: 5rem;
    }
  }

  .gh-contact {
    text-align: center;
    .gh-heading {
      margin: 0 0 1.5rem;
      color: #085078;
      font-weight: 600;
      font-size: 2.7rem;
    }

    .gh-subheading {
      margin-bottom: 1.5rem;
      font-size: 1.2rem;
      font-weight: 500;
    }
  }

  @media (max-width: 810px) {
    .gh-hero-image {
      display: none;
    }

    .gh-hero-content {
      width: 80%
    }

    .gh-intro {
      .container {
        padding: 20px;
      }
    }

    .gh-feature-item {
      width: 33%;
    }
  }

  @media (max-width: 600px) {
    .gh-intro {
      .container {
        flex-flow: row wrap
      }
    }

    .gh-intro-content {
      width: 100%;
      order: 1;
      padding: 0;
    }

    .gh-intro-image {
      height: auto;
      margin: 0 auto 2rem;
    }

    .gh-feature-item {
      width: 50%;
    }
  }

  @media (max-width: 480px) {
    .gh-hero-content {
      width: 100%;
    }

    .gh-feature-item {
      width: 100%;
    }

    .gh-course-info {
      width: 100%;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .gh-course-image {
      display: none;
    }
  }
</style>

<style lang="scss">
  .gh-credential-landing-page {
    .gh-form {
      max-width: 680px;
      margin: 2rem auto 0;

      .el-input__inner,
      .el-textarea__inner {
        background: #fff;
        border-radius: 5px;
      }
    }
  }
</style>
